import { Alert, Button, Form, Input, Radio, Spin, Switch } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { invoiceService, type CustomerInvoiceHeaderForm } from "@/api/invoiceService";
import { Breadcrumb, ZhInsetTabs, ZhMessageBox } from "@/components/common";

const headerOptions = [
    { label: "企业", value: 1 },
    { label: "个人或事业单位", value: 2 }
];

/** 发票抬头新增/编辑页按企业和个人类型动态切换字段及校验。 */
export function InvoiceFormPage() {
    const [form] = Form.useForm<CustomerInvoiceHeaderForm>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = Number(searchParams.get("id") || 0);
    const [loading, setLoading] = useState(Boolean(id));
    const [error, setError] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingValues, setPendingValues] = useState<CustomerInvoiceHeaderForm>();
    const headerType = Form.useWatch("headerType", form) ?? 1;
    const invoiceType = Form.useWatch("invoiceType", form) ?? 1;
    const pageName = id ? "修改发票抬头" : "新增发票抬头";
    /** 切换为个人抬头时清理已注册的企业字段，避免隐藏字段继续参与校验和提交。 */
    const changeHeaderType = (value: number) => {
        form.setFieldsValue(value === 2 ? {
            headerType: value,
            invoiceType: undefined,
            taxpayerNo: undefined,
            mobile: undefined,
            address: undefined,
            bank: undefined,
            account: undefined
        } : { headerType: value, invoiceType: 1 });
    };
    /** 编辑态加载详情，字段映射保持与旧版 vo2Form 一致。 */
    const load = async () => {
        if (!id) return;
        setLoading(true);
        setError(false);
        try {
            const data = await invoiceService.getInvoiceInfoById(id);
            form.setFieldsValue({
                headerType: data.headerType,
                invoiceType: data.invoiceType,
                name: data.name,
                taxpayerNo: data.taxpayerNo,
                mobile: data.mobile,
                address: data.address,
                bank: data.bank,
                account: data.account,
                isDefault: data.isDefault
            });
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    /** 表单校验通过后才打开旧版确认框，避免无效数据进入提交阶段。 */
    const requestSave = async () => {
        try {
            // Ant Design 6 会保留已卸载字段，显式限定当前抬头类型的可见字段。
            const currentHeaderType = Number(form.getFieldValue("headerType") ?? headerType);
            if (currentHeaderType === 2) {
                const name = form.getFieldValue("name");
                if (!name) {
                    await form.validateFields(["name"]);
                    return;
                }
                setPendingValues({ headerType: 2, name, isDefault: form.getFieldValue("isDefault") });
                return;
            }
            const visibleFields: Array<keyof CustomerInvoiceHeaderForm> = headerType === 2
                ? ["headerType", "name", "isDefault"]
                : ["headerType", "invoiceType", "name", "taxpayerNo", "address", "mobile", "bank", "account", "isDefault"];
            setPendingValues(await form.validateFields(visibleFields));
        } catch {
            /* 表单组件已经展示逐项校验信息。 */
        }
    };
    /** 对应旧 confirmWrapper：确认框先关闭，再调用新增或编辑接口并返回。 */
    const save = async () => {
        if (!pendingValues) return;
        const payload = { ...pendingValues, isDefault: pendingValues.isDefault ? 1 : 0 };
        setPendingValues(undefined);
        setSaving(true);
        try {
            if (id) await invoiceService.editInvoice(id, payload);
            else await invoiceService.addInvoice(payload);
            navigate(-1);
        } finally {
            setSaving(false);
        }
    };
    if (loading) return <Spin fullscreen tip="发票抬头加载中" />;
    if (error)
        return (
            <Alert
                type="error"
                showIcon
                message="发票抬头加载失败"
                action={<Button onClick={() => void load()}>重试</Button>}
            />
        );
    return (
        <section className="invoice-form-page">
            <div className="section-card invoice-form-heading">
                <Breadcrumb
                    routes={[{ name: "账号管理" }, { name: "发票抬头", path: "/me/invoice" }, { name: pageName }]}
                />
                <div className="invoice-form-title">{pageName}</div>
                <ZhInsetTabs
                    active={headerType}
                    options={headerOptions}
                    onChange={changeHeaderType}
                />
            </div>
            <div className="section-card invoice-form-card">
                <Form
                    colon={false}
                    form={form}
                    initialValues={{ headerType: 1, invoiceType: 1, isDefault: 0 }}
                    labelAlign="right"
                >
                    <Form.Item name="headerType" hidden>
                        <Input />
                    </Form.Item>
                    <div className="invoice-form-grid">
                        {headerType === 1 ? (
                            <>
                                <Form.Item
                                    name="invoiceType"
                                    label="发票类型"
                                    required
                                    rules={[{ required: true, message: "请选择发票类型" }]}
                                >
                                    <Radio.Group>
                                        <Radio value={1}>普通发票</Radio>
                                        <Radio value={2}>增值税专用发票</Radio>
                                    </Radio.Group>
                                </Form.Item>
                                <Form.Item
                                    name="name"
                                    label="名称"
                                    required
                                    rules={[{ required: true, message: "请输入名称" }]}
                                >
                                    <Input maxLength={40} showCount placeholder="请输入名称" />
                                </Form.Item>
                                <Form.Item
                                    name="taxpayerNo"
                                    label="纳税号"
                                    required
                                    rules={[
                                        { required: true, message: "请输入纳税人识别号" },
                                        { pattern: /^[0-9A-Z]{18}$/, message: "请输入正确的纳税人识别号" }
                                    ]}
                                >
                                    <Input maxLength={18} showCount placeholder="请输入纳税号" />
                                </Form.Item>
                                <Form.Item
                                    name="address"
                                    label="地址"
                                    required={invoiceType === 2}
                                    rules={[{
                                        validator: (_, value) => form.getFieldValue("invoiceType") === 2 && !value
                                            ? Promise.reject(new Error("请输入公司注册地址"))
                                            : Promise.resolve()
                                    }]}
                                >
                                    <Input maxLength={128} showCount placeholder="请输入详细地址" />
                                </Form.Item>
                                <Form.Item
                                    name="mobile"
                                    label="电话"
                                    required={invoiceType === 2}
                                    rules={[
                                        { required: true, message: "请输入手机号" },
                                        { pattern: /^1[3456789]\d{9}$/, message: "请输入正确的手机号" }
                                    ]}
                                >
                                    <Input placeholder="请输入电话" />
                                </Form.Item>
                                <Form.Item
                                    name="bank"
                                    label="开户行"
                                    required={invoiceType === 2}
                                    rules={[{ required: invoiceType === 2, message: "请输入对公账户开户行" }]}
                                >
                                    <Input maxLength={128} showCount placeholder="请输入开户行" />
                                </Form.Item>
                                <Form.Item
                                    name="account"
                                    label="账号"
                                    required={invoiceType === 2}
                                    rules={[
                                        { required: invoiceType === 2, message: "请输入对公账户银行账号" },
                                        { pattern: /^\d{8,30}$/, message: "请输入格式正确的对公账户银行账号" }
                                    ]}
                                >
                                    <Input maxLength={20} showCount placeholder="请输入账号" />
                                </Form.Item>
                                <Form.Item
                                    name="isDefault"
                                    label="设为默认抬头"
                                    valuePropName="checked"
                                    getValueProps={value => ({ checked: Boolean(value) })}
                                >
                                    <Switch />
                                </Form.Item>
                            </>
                        ) : (
                            <>
                                <Form.Item
                                    name="name"
                                    label="名称"
                                    required
                                    rules={[{ required: true, message: "请输入名称" }]}
                                >
                                    <Input maxLength={40} showCount placeholder="请输入名称" />
                                </Form.Item>
                                <Form.Item
                                    name="isDefault"
                                    label="设为默认抬头"
                                    valuePropName="checked"
                                    getValueProps={value => ({ checked: Boolean(value) })}
                                >
                                    <Switch />
                                </Form.Item>
                            </>
                        )}
                    </div>
                </Form>
            </div>
            <div className="invoice-form-footer">
                <Button type="primary" loading={saving} onClick={() => void requestSave()}>
                    提交
                </Button>
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
            <ZhMessageBox
                open={Boolean(pendingValues)}
                tip="是否提交"
                onCancel={() => setPendingValues(undefined)}
                onSubmit={save}
            />
        </section>
    );
}
